import { forwardRef } from 'react';
import { useController } from 'react-hook-form';

import { NumberInput, NumberInputProps } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';

import { useReadOnlyPlaceholder } from './hooks/use-readonly-placeholder';

export type NumberFieldProps = NumberInputProps & {
    name: string;
    afterChange?: (value: number | null) => void;
};

const NumberField = forwardRef<HTMLInputElement, NumberFieldProps>(
    ({ name, afterChange, ...props }: NumberFieldProps, forwardedRef) => {
        const {
            field: { onChange, ...field },
            fieldState: { error, isTouched },
        } = useController({ name });

        const placeholderRef = useReadOnlyPlaceholder('Not specified');

        const ref = useMergedRef(forwardedRef, placeholderRef);

        return (
            <NumberInput
                {...field}
                {...props}
                onValueChange={(values, { event }) => {
                    if (props.min !== undefined && values.floatValue !== undefined && values.floatValue < props.min) {
                        event?.preventDefault();
                        return;
                    }

                    if (props.max !== undefined && values.floatValue !== undefined && values.floatValue > props.max) {
                        event?.preventDefault();
                        return;
                    }

                    onChange(values.floatValue);

                    if (afterChange) {
                        afterChange(values.floatValue ?? null);
                    }
                }}
                value={field.value ?? ''}
                error={isTouched && error?.message}
                ref={ref}
            />
        );
    },
);

NumberField.displayName = 'NumberField';

export { NumberField };
